import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { formAPI } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Plus, FileText, ExternalLink, Copy, Trash2, 
  ToggleLeft, ToggleRight, Calendar, Users, Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { AiWizardStatsBlock } from '@/components/AiWizardStatsBlock';

export function FormsListPage() {
  const { t, language } = useLanguage();
  const [forms, setForms] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadForms();
  }, []);

  const loadForms = async () => {
    try {
      const response = await formAPI.list();
      setForms(response.data);
    } catch (err) {
      console.error('Error loading forms:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const copyEmbedCode = (form) => {
    const backendUrl = window.location.origin;
    const embedCode = `<iframe src="${backendUrl}/soumission/${form.slug}" width="100%" height="600" frameborder="0"></iframe>`;
    navigator.clipboard.writeText(embedCode);
    toast.success(t('forms.embedCopied'));
  };

  const toggleFormStatus = async (form) => {
    try {
      await formAPI.update(form.form_id, { is_active: !form.is_active });
      setForms(prev => prev.map(f => 
        f.form_id === form.form_id ? { ...f, is_active: !f.is_active } : f
      ));
      toast.success(form.is_active ? t('forms.formDeactivated') : t('forms.formActivated'));
    } catch (err) {
      console.error('Error toggling form:', err);
      toast.error(t('common.error'));
    }
  };

  const deleteForm = async (formId) => {
    if (!window.confirm(t('forms.deleteConfirm'))) return;
    
    try {
      await formAPI.delete(formId);
      setForms(prev => prev.filter(f => f.form_id !== formId));
      toast.success(t('forms.formDeleted'));
    } catch (err) {
      console.error('Error deleting form:', err);
      toast.error(t('common.error'));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const locale = language === 'fr' ? 'fr-CA' : 'en-CA';
    return new Date(dateStr).toLocaleDateString(locale, {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="forms-list-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-black tracking-tight">{t('forms.title')}</h1>
          <p className="text-muted-foreground mt-1">
            {t('forms.description')}
          </p>
        </div>
        <Button asChild data-testid="new-form-btn">
          <Link to="/forms/new">
            <Plus className="mr-2 h-4 w-4" />
            {t('forms.newForm')}
          </Link>
        </Button>
      </div>

      {/* AI Wizard funnel stats — only when the workspace has AI forms */}
      {forms.some((f) => f.form_type === 'ai_estimation') && <AiWizardStatsBlock />}

      {/* Forms Grid */}
      {forms.length === 0 ? (
        <Card className="bg-muted/30">
          <CardContent className="py-16 text-center">
            <FileText className="h-12 w-12 mx-auto mb-4 text-muted-foreground opacity-50" />
            <h3 className="text-xl font-bold mb-2">{t('forms.noForms')}</h3>
            <p className="text-muted-foreground mb-6 max-w-md mx-auto">
              {t('forms.noFormsDesc')}
            </p>
            <Button asChild>
              <Link to="/forms/new">
                <Plus className="mr-2 h-4 w-4" />
                {t('forms.createForm')}
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {forms.map((form) => (
            <Card key={form.form_id} className="hover:shadow-md transition-shadow">
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <CardTitle className="text-lg font-bold truncate flex items-center gap-2">
                      {form.form_type === 'ai_estimation' && (
                        <Sparkles className="h-4 w-4 text-amber-500 shrink-0" />
                      )}
                      <span className="truncate">{form.name}</span>
                    </CardTitle>
                    <CardDescription className="mt-1 line-clamp-2">
                      {form.description || t('common.noData')}
                    </CardDescription>
                  </div>
                  <div className="flex flex-col items-end gap-1.5 shrink-0">
                    <Badge variant={form.is_active ? 'default' : 'secondary'}>
                      {form.is_active ? t('forms.active') : t('forms.inactive')}
                    </Badge>
                    {form.form_type === 'ai_estimation' && (
                      <Badge
                        variant="outline"
                        className="text-[10px] border-amber-500/50 text-amber-700 bg-amber-500/10"
                        data-testid={`form-ai-badge-${form.form_id}`}
                      >
                        {language === 'fr' ? 'IA Estimation' : 'AI Estimation'}
                      </Badge>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Stats */}
                <div className="flex items-center gap-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <FileText className="h-4 w-4" />
                    {form.fields?.length || 0} {t('forms.fields')}
                  </span>
                  <span className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    {form.submissions_count || 0} {t('forms.submissions')}
                  </span>
                </div>

                {/* Slug */}
                <div className="p-2 bg-muted/50 rounded font-mono text-xs truncate">
                  /soumission/{form.slug}
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 pt-2">
                  <Button asChild variant="outline" size="sm" className="flex-1">
                    <Link to={`/forms/${form.form_id}`}>
                      {t('common.edit')}
                    </Link>
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => copyEmbedCode(form)}
                    title={t('forms.copyEmbed')}
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => toggleFormStatus(form)}
                    title={form.is_active ? t('forms.inactive') : t('forms.active')}
                  >
                    {form.is_active ? (
                      <ToggleRight className="h-4 w-4 text-green-600" />
                    ) : (
                      <ToggleLeft className="h-4 w-4" />
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => deleteForm(form.form_id)}
                    title={t('common.delete')}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
